/**
 * Nutrition Service
 *
 * Handles meal planning, food logging, and nutrition tracking API calls
 */

import { HTTP } from "./http";

let API_SERVER = "";
if (window.location.href.indexOf("8080") > -1) {
  API_SERVER = "http://www.localhost:8888/back-end-endpoint-nutrition.php";
} else {
  API_SERVER = "/personal-training-BE/back-end-endpoint-nutrition.php";
}

const Nutrition = {
  // =========================================================================
  // MEAL LOGGING
  // =========================================================================

  /**
   * Log a meal
   * @param {Object} mealData - Meal data including user_id, meal_type, meal_date, macros
   */
  logMeal(mealData) {
    return HTTP.post(API_SERVER, {
      action: "logMeal",
      ...mealData,
    });
  },

  /**
   * Update a logged meal
   * @param {number} mealId - ID of the meal to update
   * @param {Object} mealData - Updated meal data
   */
  updateMeal(mealId, mealData) {
    return HTTP.post(API_SERVER, {
      action: "updateMeal",
      meal_id: mealId,
      ...mealData,
    });
  },

  /**
   * Delete a logged meal
   * @param {number} mealId - ID of the meal to delete
   * @param {number} userId - User ID for verification
   */
  deleteMeal(mealId, userId) {
    return HTTP.post(API_SERVER, {
      action: "deleteMeal",
      meal_id: mealId,
      user_id: userId,
    });
  },

  // =========================================================================
  // DAILY & WEEKLY PLANS
  // =========================================================================

  /**
   * Get daily meal plan with context
   * @param {number} userId - User ID
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {Object} options - Optional: club_id, team_id, weight
   */
  getDailyPlan(userId, date, options = {}) {
    return HTTP.post(API_SERVER, {
      action: "getDailyPlan",
      user_id: userId,
      date: date,
      club_id: options.clubId || null,
      team_id: options.teamId || null,
      weight: options.weight || null,
    });
  },

  /**
   * Get weekly meal plan overview
   * @param {number} userId - User ID
   * @param {string} startDate - Start date of the week (Monday)
   * @param {Object} options - Optional: club_id, team_id, weight
   */
  getWeeklyPlan(userId, startDate, options = {}) {
    return HTTP.post(API_SERVER, {
      action: "getWeeklyPlan",
      user_id: userId,
      start_date: startDate,
      club_id: options.clubId || null,
      team_id: options.teamId || null,
      weight: options.weight || null,
    });
  },

  /**
   * Get daily summary with progress
   * @param {number} userId - User ID
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {Object} options - Optional: club_id, team_id, weight
   */
  getDailySummary(userId, date, options = {}) {
    return HTTP.post(API_SERVER, {
      action: "getDailySummary",
      user_id: userId,
      date: date,
      club_id: options.clubId || null,
      team_id: options.teamId || null,
      weight: options.weight || null,
    });
  },

  // =========================================================================
  // MEAL TEMPLATES
  // =========================================================================

  /**
   * Get meal templates/suggestions
   * @param {Object} options - Optional: club_id, context, meal_type
   */
  getMealTemplates(options = {}) {
    return HTTP.post(API_SERVER, {
      action: "getMealTemplates",
      club_id: options.clubId || null,
      context: options.context || null,
      meal_type: options.mealType || null,
    });
  },

  // =========================================================================
  // PLAYER GOALS
  // =========================================================================

  /**
   * Get player nutrition goals
   * @param {number} userId - User ID
   */
  getPlayerGoals(userId) {
    return HTTP.post(API_SERVER, {
      action: "getPlayerGoals",
      user_id: userId,
    });
  },

  /**
   * Save player nutrition goals
   * @param {Object} goals - Goals including user_id, custom targets, restrictions
   */
  savePlayerGoals(goals) {
    return HTTP.post(API_SERVER, {
      action: "savePlayerGoals",
      ...goals,
    });
  },

  // =========================================================================
  // HYDRATION
  // =========================================================================

  /**
   * Log hydration intake
   * @param {Object} data - { user_id, amount_ml, drink_type, log_date, log_time }
   */
  logHydration(data) {
    return HTTP.post(API_SERVER, {
      action: "logHydration",
      ...data,
    });
  },

  /**
   * Get hydration logs for a date
   * @param {number} userId - User ID
   * @param {string} date - Date in YYYY-MM-DD format
   */
  getHydrationLogs(userId, date) {
    return HTTP.post(API_SERVER, {
      action: "getHydrationLogs",
      user_id: userId,
      log_date: date,
    });
  },

  /**
   * Delete a hydration log entry
   * @param {number} logId - Hydration log ID
   * @param {number} userId - User ID
   */
  deleteHydration(logId, userId) {
    return HTTP.post(API_SERVER, {
      action: "deleteHydration",
      log_id: logId,
      user_id: userId,
    });
  },

  // =========================================================================
  // UTILITY METHODS
  // =========================================================================

  /**
   * Get meal type display info
   * @param {string} mealType - Meal type identifier
   */
  getMealTypeInfo(mealType) {
    const mealTypes = {
      breakfast: {
        label: "Breakfast",
        icon: "sunrise",
        symbol: "B",
        time: "07:00-09:00",
        color: "#ffc107",
      },
      lunch: {
        label: "Lunch",
        icon: "sun",
        symbol: "L",
        time: "12:00-14:00",
        color: "#28a745",
      },
      dinner: {
        label: "Dinner",
        icon: "moon",
        symbol: "D",
        time: "18:00-20:00",
        color: "#6f42c1",
      },
      snack: {
        label: "Snack",
        icon: "apple-alt",
        symbol: "S",
        time: "Any time",
        color: "#17a2b8",
      },
      pre_training: {
        label: "Pre-Training",
        icon: "running",
        symbol: "PT",
        time: "1-2 hrs before",
        color: "#fd7e14",
      },
      post_training: {
        label: "Post-Training",
        icon: "dumbbell",
        symbol: "R",
        time: "Within 60 mins",
        color: "#20c997",
      },
      pre_match: {
        label: "Pre-Match",
        icon: "futbol",
        symbol: "PM",
        time: "3-4 hrs before",
        color: "#dc3545",
      },
      post_match: {
        label: "Post-Match",
        icon: "trophy",
        symbol: "MR",
        time: "Within 60 mins",
        color: "#007bff",
      },
    };
    return mealTypes[mealType] || mealTypes.snack;
  },

  /**
   * Get all meal types for dropdown
   */
  getAllMealTypes() {
    return [
      { value: "breakfast", label: "Breakfast", symbol: "B", color: "#ffc107" },
      { value: "lunch", label: "Lunch", symbol: "L", color: "#28a745" },
      { value: "dinner", label: "Dinner", symbol: "D", color: "#6f42c1" },
      { value: "snack", label: "Snack", symbol: "S", color: "#17a2b8" },
      { value: "pre_training", label: "Pre-Training", symbol: "PT", color: "#fd7e14" },
      { value: "post_training", label: "Post-Training", symbol: "R", color: "#20c997" },
      { value: "pre_match", label: "Pre-Match", symbol: "PM", color: "#dc3545" },
      { value: "post_match", label: "Post-Match", symbol: "MR", color: "#007bff" },
    ];
  },

  /**
   * Get context display info
   * @param {string} context - Day context
   */
  getContextInfo(context) {
    const contexts = {
      match_day: {
        label: "Match Day",
        shortLabel: "Match",
        color: "#dc3545",
        bgColor: "#f8d7da",
        description: "High carbs for performance",
      },
      carb_loading: {
        label: "Carb Loading (MD-1)",
        shortLabel: "MD-1",
        color: "#fd7e14",
        bgColor: "#ffe5d0",
        description: "Maximize glycogen stores",
      },
      preparation: {
        label: "Preparation (MD-2)",
        shortLabel: "MD-2",
        color: "#ffc107",
        bgColor: "#fff3cd",
        description: "Begin increasing carbs",
      },
      recovery_day: {
        label: "Recovery (MD+1)",
        shortLabel: "MD+1",
        color: "#28a745",
        bgColor: "#d4edda",
        description: "Replenish and repair",
      },
      training_day: {
        label: "Training Day",
        shortLabel: "Training",
        color: "#007bff",
        bgColor: "#cce5ff",
        description: "Standard training nutrition",
      },
      rest_day: {
        label: "Rest Day",
        shortLabel: "Rest",
        color: "#6c757d",
        bgColor: "#e9ecef",
        description: "Lower energy needs",
      },
    };
    return contexts[context] || contexts.training_day;
  },

  /**
   * Format macros for display
   * @param {Object} macros - { carbs, protein, fat, calories }
   */
  formatMacros(macros) {
    return {
      carbs: `${Math.round(macros.carbs || 0)}g`,
      protein: `${Math.round(macros.protein || 0)}g`,
      fat: `${Math.round(macros.fat || 0)}g`,
      calories: `${Math.round(macros.calories || 0)} kcal`,
    };
  },

  /**
   * Calculate calories from macros
   * @param {number} carbs - Grams of carbs
   * @param {number} protein - Grams of protein
   * @param {number} fat - Grams of fat
   */
  calculateCalories(carbs, protein, fat) {
    return Math.round((carbs || 0) * 4 + (protein || 0) * 4 + (fat || 0) * 9);
  },

  /**
   * Get drink types for hydration logging
   */
  getDrinkTypes() {
    return [
      { value: "water", label: "Water", symbol: "W", color: "#17a2b8" },
      { value: "sports_drink", label: "Sports Drink", symbol: "SD", color: "#fd7e14" },
      { value: "electrolytes", label: "Electrolytes", symbol: "E", color: "#ffc107" },
      { value: "protein_shake", label: "Protein Shake", symbol: "PS", color: "#6f42c1" },
      { value: "other", label: "Other", symbol: "O", color: "#6c757d" },
    ];
  },

  /**
   * Get common portion sizes for quick logging
   */
  getQuickPortions() {
    return [
      { ml: 200, label: "Small glass" },
      { ml: 330, label: "Can" },
      { ml: 500, label: "Bottle" },
      { ml: 750, label: "Large bottle" },
      { ml: 1000, label: "1 Litre" },
    ];
  },

  // =========================================================================
  // PHASE 2: SMART RECOMMENDATIONS
  // =========================================================================

  /**
   * Get smart recommendations based on match timing
   * @param {number} userId - User ID
   * @param {number} teamId - Team ID
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} currentTime - Current time in HH:MM:SS format (optional)
   */
  getSmartRecommendations(userId, teamId, date, currentTime = null) {
    return HTTP.post(API_SERVER, {
      action: "getSmartRecommendations",
      user_id: userId,
      team_id: teamId,
      date: date,
      current_time: currentTime,
    });
  },

  /**
   * Get hydration plan for match day
   * @param {number} userId - User ID
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {Object} options - Optional: team_id, weight
   */
  getHydrationPlan(userId, date, options = {}) {
    return HTTP.post(API_SERVER, {
      action: "getHydrationPlan",
      user_id: userId,
      date: date,
      team_id: options.teamId || null,
      weight: options.weight || null,
    });
  },

  // =========================================================================
  // PHASE 2: FAVORITES
  // =========================================================================

  /**
   * Toggle favorite status for a meal template
   * @param {number} userId - User ID
   * @param {number} templateId - Meal template ID
   */
  toggleFavorite(userId, templateId) {
    return HTTP.post(API_SERVER, {
      action: "toggleFavorite",
      user_id: userId,
      template_id: templateId,
    });
  },

  /**
   * Get player's favorite meal templates
   * @param {number} userId - User ID
   */
  getFavorites(userId) {
    return HTTP.post(API_SERVER, {
      action: "getFavorites",
      user_id: userId,
    });
  },

  /**
   * Get filtered meal templates with dietary restrictions
   * @param {Object} options - Filter options
   * @param {string} options.context - Day context filter
   * @param {string} options.mealType - Meal type filter
   * @param {string} options.category - Category filter (quick, main, light, etc.)
   * @param {Array} options.dietary - Dietary restriction filters
   * @param {boolean} options.favoritesOnly - Only show favorites
   * @param {string} options.search - Search term
   * @param {number} options.userId - User ID (for favorite status)
   */
  getFilteredTemplates(options = {}) {
    return HTTP.post(API_SERVER, {
      action: "getFilteredTemplates",
      context: options.context || null,
      meal_type: options.mealType || null,
      category: options.category || null,
      dietary: options.dietary || [],
      favorites_only: options.favoritesOnly || false,
      search: options.search || null,
      user_id: options.userId || null,
    });
  },

  // =========================================================================
  // PHASE 2: REMINDERS
  // =========================================================================

  /**
   * Save a nutrition reminder
   * @param {Object} reminder - Reminder data
   * @param {number} reminder.user_id - User ID
   * @param {string} reminder.reminder_type - Type (hydration, pre_match_meal, etc.)
   * @param {string} reminder.reminder_time - Time in HH:MM format
   * @param {Array} reminder.days_active - Days array ['mon', 'tue', etc.]
   * @param {boolean} reminder.is_enabled - Whether enabled
   * @param {string} reminder.message - Custom message
   */
  saveReminder(reminder) {
    return HTTP.post(API_SERVER, {
      action: "saveReminder",
      ...reminder,
    });
  },

  /**
   * Get player's nutrition reminders
   * @param {number} userId - User ID
   */
  getReminders(userId) {
    return HTTP.post(API_SERVER, {
      action: "getReminders",
      user_id: userId,
    });
  },

  /**
   * Delete a nutrition reminder
   * @param {number} reminderId - Reminder ID
   * @param {number} userId - User ID
   */
  deleteReminder(reminderId, userId) {
    return HTTP.post(API_SERVER, {
      action: "deleteReminder",
      reminder_id: reminderId,
      user_id: userId,
    });
  },

  /**
   * Check for active reminders around current time
   * @param {number} userId - User ID
   * @param {string} currentTime - Current time in HH:MM:SS format (optional)
   */
  checkActiveReminders(userId, currentTime = null) {
    return HTTP.post(API_SERVER, {
      action: "checkActiveReminders",
      user_id: userId,
      current_time: currentTime,
    });
  },

  // =========================================================================
  // PHASE 2: UTILITY METHODS
  // =========================================================================

  /**
   * Get meal timing windows relative to match time
   * @param {string} matchTime - Match time in HH:MM format
   * @returns {Object} Meal timing windows
   */
  getMatchDaySchedule(matchTime) {
    const match = new Date(`2000-01-01 ${matchTime}`);
    const matchMs = match.getTime();

    return {
      preMatchMeal: {
        start: new Date(matchMs - 4 * 60 * 60 * 1000).toTimeString().slice(0, 5),
        end: new Date(matchMs - 3 * 60 * 60 * 1000).toTimeString().slice(0, 5),
        label: "Pre-Match Meal (3-4 hrs before)",
      },
      preMatchSnack: {
        start: new Date(matchMs - 2 * 60 * 60 * 1000).toTimeString().slice(0, 5),
        end: new Date(matchMs - 1 * 60 * 60 * 1000).toTimeString().slice(0, 5),
        label: "Pre-Match Snack (1-2 hrs before)",
      },
      matchTime: matchTime,
      postMatch: {
        start: matchTime,
        end: new Date(matchMs + 105 * 60 * 1000 + 60 * 60 * 1000)
          .toTimeString()
          .slice(0, 5),
        label: "Recovery Window (within 60 mins)",
      },
    };
  },

  /**
   * Get dietary tag options for filtering
   */
  getDietaryTags() {
    return [
      { value: "vegetarian", label: "Vegetarian", color: "#28a745" },
      { value: "vegan", label: "Vegan", color: "#20c997" },
      { value: "gluten-free", label: "Gluten-Free", color: "#ffc107" },
      { value: "dairy-free", label: "Dairy-Free", color: "#17a2b8" },
    ];
  },

  /**
   * Get category options for recipe filtering
   */
  getCategories() {
    return [
      { value: "quick", label: "Quick (<15 min)", color: "#28a745" },
      { value: "main", label: "Main Meal", color: "#007bff" },
      { value: "light", label: "Light", color: "#17a2b8" },
      { value: "hearty", label: "Hearty", color: "#fd7e14" },
      { value: "liquid", label: "Liquid/Shake", color: "#6f42c1" },
    ];
  },

  /**
   * Get difficulty levels
   */
  getDifficultyLevels() {
    return [
      { value: "easy", label: "Easy", color: "#28a745" },
      { value: "medium", label: "Medium", color: "#ffc107" },
      { value: "advanced", label: "Advanced", color: "#dc3545" },
    ];
  },

  /**
   * Get reminder types
   */
  getReminderTypes() {
    return [
      {
        value: "hydration",
        label: "Hydration",
        icon: "H",
        color: "#17a2b8",
        description: "Reminder to drink water",
      },
      {
        value: "pre_match_meal",
        label: "Pre-Match Meal",
        icon: "PM",
        color: "#dc3545",
        description: "Eat 3-4 hours before match",
      },
      {
        value: "post_match_meal",
        label: "Post-Match Recovery",
        icon: "MR",
        color: "#007bff",
        description: "Eat within 60 mins after match",
      },
      {
        value: "general",
        label: "General",
        icon: "N",
        color: "#6c757d",
        description: "Custom nutrition reminder",
      },
    ];
  },

  // =========================================================================
  // ADMIN/COACH: TEAM NUTRITION OVERVIEW
  // =========================================================================

  /**
   * Get team nutrition overview (all players with today's nutrition summary)
   * @param {number} clubId - Club ID (for admins)
   * @param {number} coachId - Coach ID (for coaches to get their players)
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {string} userType - User type ('Admin' or 'Coach')
   */
  getTeamNutritionOverview(clubId, coachId, date, userType = null) {
    return HTTP.post(API_SERVER, {
      action: "getTeamNutritionOverview",
      club_id: clubId,
      coach_id: coachId,
      date: date,
      user_type: userType || (coachId ? "Coach" : "Admin"),
    });
  },

  /**
   * Get team nutrition dashboard with analytics
   * @param {number} clubId - Club ID
   * @param {number} teamId - Team ID (optional, filter by team)
   * @param {Object} dateRange - { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }
   * @param {number} coachId - Coach ID (for coaches)
   */
  getTeamNutritionDashboard(clubId, teamId, dateRange = {}, coachId = null) {
    return HTTP.post(API_SERVER, {
      action: "getTeamNutritionDashboard",
      club_id: clubId,
      team_id: teamId,
      coach_id: coachId,
      user_type: coachId ? "Coach" : "Admin",
      start_date: dateRange.start || null,
      end_date: dateRange.end || null,
    });
  },

  /**
   * Fuelling usage/engagement for coaches: per-player opens, last used, and a
   * pathway breakdown over the last `days` days.
   */
  getNutritionUsage(clubId, teamId = null, days = 30) {
    return HTTP.post(API_SERVER, {
      action: "getNutritionUsage",
      club_id: clubId,
      team_id: teamId,
      days,
    });
  },

  // --- Fuelling estimator (U16+) — staff-facing ---

  /** Staff monitoring: BMR/energy estimate + weight trend + flags for a player. */
  getPlayerFuellingEstimate(userId, date = null, days = 90) {
    return HTTP.post(API_SERVER, {
      action: "getPlayerFuellingEstimate",
      user_id: userId,
      date,
      days,
    });
  },

  /** Staff: set a player's sex (required for the BMR equation). */
  setPlayerGender(userId, gender) {
    return HTTP.post(API_SERVER, {
      action: "setPlayerGender",
      user_id: userId,
      gender,
    });
  },

  /** Staff: record/revoke parental consent for the estimator. */
  setEstimatorConsent(userId, consented, consentedBy = null, note = null) {
    return HTTP.post(API_SERVER, {
      action: "setEstimatorConsent",
      user_id: userId,
      consented,
      consented_by: consentedBy,
      note,
    });
  },

  /**
   * Staff: squad fuelling-readiness list (sex/DOB/consent/weight per player).
   * Scope like the other staff views: Admin => club, Coach => own players.
   */
  getSquadReadiness(clubId, coachId = null, userType = "Admin") {
    return HTTP.post(API_SERVER, {
      action: "getSquadReadiness",
      club_id: clubId,
      coach_id: coachId,
      user_type: userType,
    });
  },

  /**
   * Staff: batch-set players' sex / DOB / consent.
   * @param {Array<{user_id:number, gender?:string, dob?:string, consented?:boolean}>} updates
   */
  bulkSetPlayerBasics(updates, consentedBy = null) {
    return HTTP.post(API_SERVER, {
      action: "bulkSetPlayerBasics",
      updates,
      consented_by: consentedBy,
    });
  },

  /** Coach/admin: nutrition compliance trend series (club-wide or a team). */
  getComplianceTrends(clubId, teamId = null, days = 30) {
    return HTTP.post(API_SERVER, {
      action: "getComplianceTrends",
      club_id: clubId,
      team_id: teamId,
      days,
    });
  },

  /** Player/parent: what may this player see (booleans only, never a number). */
  getMyFuellingAccess(userId) {
    return HTTP.post(API_SERVER, {
      action: "getMyFuellingAccess",
      user_id: userId,
    });
  },

  /** Player/parent: self-serve parent/guardian consent for an under-18. */
  giveParentConsent(userId, guardianName = null) {
    return HTTP.post(API_SERVER, {
      action: "giveParentConsent",
      user_id: userId,
      attested: true,
      guardian_name: guardianName,
    });
  },

  /**
   * Get player nutrition data as admin/coach
   * @param {number} adminUserId - Admin or Coach user ID
   * @param {number} targetPlayerId - Player ID to view
   * @param {string} date - Date in YYYY-MM-DD format
   */
  getPlayerNutritionForAdmin(adminUserId, targetPlayerId, date) {
    return HTTP.post(API_SERVER, {
      action: "getPlayerNutritionForAdmin",
      admin_user_id: adminUserId,
      target_player_id: targetPlayerId,
      date: date,
    });
  },

  // =========================================================================
  // ADMIN/COACH: RECIPE/TEMPLATE MANAGEMENT
  // =========================================================================

  /**
   * Get all meal templates for admin management (including inactive)
   * @param {number} clubId - Club ID
   * @param {boolean} includeInactive - Include inactive templates
   */
  getAllMealTemplates(clubId, includeInactive = true) {
    return HTTP.post(API_SERVER, {
      action: "getAllMealTemplates",
      club_id: clubId,
      include_inactive: includeInactive,
    });
  },

  /**
   * Create a new meal template
   * @param {Object} templateData - Template data
   * @param {string} templateData.name - Template name
   * @param {string} templateData.description - Description
   * @param {string} templateData.meal_type - Meal type (breakfast, lunch, etc.)
   * @param {string} templateData.context - Day context (match_day, training, etc.)
   * @param {string} templateData.category - Category (quick, main, light, etc.)
   * @param {string} templateData.dietary_tags - Comma-separated dietary tags
   * @param {string} templateData.ingredients - Ingredients list
   * @param {number} templateData.calories - Calories
   * @param {number} templateData.carbs - Carbs in grams
   * @param {number} templateData.protein - Protein in grams
   * @param {number} templateData.fat - Fat in grams
   * @param {number} templateData.club_id - Club ID (optional, for club-specific templates)
   */
  createMealTemplate(templateData) {
    return HTTP.post(API_SERVER, {
      action: "createMealTemplate",
      ...templateData,
    });
  },

  /**
   * Update an existing meal template
   * @param {number} templateId - Template ID
   * @param {Object} templateData - Updated template data
   */
  updateMealTemplate(templateId, templateData) {
    return HTTP.post(API_SERVER, {
      action: "updateMealTemplate",
      template_id: templateId,
      ...templateData,
    });
  },

  /**
   * Delete a meal template (soft delete - sets is_active = 0)
   * @param {number} templateId - Template ID
   */
  deleteMealTemplate(templateId) {
    return HTTP.post(API_SERVER, {
      action: "deleteMealTemplate",
      template_id: templateId,
    });
  },

  /**
   * Toggle template active status
   * @param {number} templateId - Template ID
   * @param {boolean} isActive - Whether template should be active
   */
  toggleTemplateActive(templateId, isActive) {
    return HTTP.post(API_SERVER, {
      action: "toggleTemplateActive",
      template_id: templateId,
      is_active: isActive,
    });
  },

  // =========================================================================
  // ADMIN/COACH: UTILITY METHODS
  // =========================================================================

  /**
   * Get compliance status color and label
   * @param {number} percentage - Compliance percentage
   */
  getComplianceStatus(percentage) {
    if (percentage >= 80) {
      return { label: "Good", color: "#28a745", bgColor: "#d4edda" };
    } else if (percentage >= 50) {
      return { label: "Moderate", color: "#ffc107", bgColor: "#fff3cd" };
    } else if (percentage > 0) {
      return { label: "Low", color: "#dc3545", bgColor: "#f8d7da" };
    }
    return { label: "None", color: "#6c757d", bgColor: "#e9ecef" };
  },

  /**
   * Get player nutrition status for display
   * @param {Object} playerData - Player nutrition data from overview
   */
  getPlayerNutritionStatus(playerData) {
    const hydrationPct = playerData.hydration_pct || 0;
    const mealsLogged = playerData.meals_logged || 0;
    const compliance = playerData.compliance || 0;

    // Determine overall status
    if (mealsLogged === 0 && hydrationPct === 0) {
      return {
        status: "not_logged",
        label: "Not Logged",
        color: "#6c757d",
        bgColor: "#e9ecef",
        icon: "x",
      };
    } else if (compliance >= 80) {
      return {
        status: "good",
        label: "On Track",
        color: "#28a745",
        bgColor: "#d4edda",
        icon: "check",
      };
    } else if (compliance >= 50) {
      return {
        status: "moderate",
        label: "Needs Attention",
        color: "#ffc107",
        bgColor: "#fff3cd",
        icon: "alert",
      };
    }
    return {
      status: "low",
      label: "Behind",
      color: "#dc3545",
      bgColor: "#f8d7da",
      icon: "warning",
    };
  },

  // =========================================================================
  // PHASE 3: ADVANCED INTEGRATION
  // =========================================================================

  /**
   * Get team-wide nutrition compliance dashboard
   * @param {number} clubId - Club ID
   * @param {number|null} teamId - Team ID (optional, for filtering)
   * @param {Object} dateRange - { start, end } dates in YYYY-MM-DD format
   */
  getTeamNutritionDashboard(clubId, teamId = null, dateRange) {
    return HTTP.post(API_SERVER, {
      action: "getTeamNutritionDashboard",
      club_id: clubId,
      team_id: teamId,
      start_date: dateRange.start,
      end_date: dateRange.end,
    });
  },

  /**
   * Get player wearable data summary for nutrition adjustments
   * @param {number} userId - User ID
   * @param {string} date - Date in YYYY-MM-DD format
   */
  getWearableNutritionData(userId, date) {
    return HTTP.post(API_SERVER, {
      action: "getWearableNutritionData",
      user_id: userId,
      date: date,
    });
  },

  /**
   * Save wearable-adjusted caloric targets
   * @param {number} userId - User ID
   * @param {string} date - Date
   * @param {Object} adjustments - { base_calories, activity_addition, total_calories, source }
   */
  saveWearableCalorieAdjustment(userId, date, adjustments) {
    return HTTP.post(API_SERVER, {
      action: "saveWearableCalorieAdjustment",
      user_id: userId,
      date: date,
      ...adjustments,
    });
  },

  /**
   * Log a food item from barcode scan
   * @param {Object} foodData - Food data from Nutritionix lookup
   * @param {number} userId - User ID
   * @param {string} mealType - Type of meal
   * @param {string} date - Date in YYYY-MM-DD format
   */
  logScannedFood(foodData, userId, mealType, date) {
    return HTTP.post(API_SERVER, {
      action: "logScannedFood",
      food_data: foodData,
      user_id: userId,
      meal_type: mealType,
      date: date,
    });
  },

  /**
   * Get player's food scan history
   * @param {number} userId - User ID
   * @param {number} limit - Number of records to return (default 20)
   */
  getFoodScanHistory(userId, limit = 20) {
    return HTTP.post(API_SERVER, {
      action: "getFoodScanHistory",
      user_id: userId,
      limit: limit,
    });
  },

  /**
   * Get player supplement tracking
   * @param {number} userId - User ID
   * @param {string} date - Date in YYYY-MM-DD format
   */
  getSupplementTracking(userId, date) {
    return HTTP.post(API_SERVER, {
      action: "getSupplementTracking",
      user_id: userId,
      date: date,
    });
  },

  /**
   * Log supplement intake
   * @param {number} userId - User ID
   * @param {string} supplementId - Supplement identifier
   * @param {string} date - Date
   * @param {string} timing - When taken (e.g., "morning", "pre_training")
   */
  logSupplement(userId, supplementId, date, timing) {
    return HTTP.post(API_SERVER, {
      action: "logSupplement",
      user_id: userId,
      supplement_id: supplementId,
      date: date,
      timing: timing,
    });
  },

  /**
   * Get training-based caloric recommendations
   * @param {number} userId - User ID
   * @param {string} date - Date
   * @param {Object} trainingData - GPS/training load data
   */
  getTrainingCaloricNeeds(userId, date, trainingData = null) {
    return HTTP.post(API_SERVER, {
      action: "getTrainingCaloricNeeds",
      user_id: userId,
      date: date,
      training_data: trainingData,
    });
  },

  /**
   * Calculate caloric needs based on GPS data
   * @param {Object} gpsData - { distance_km, duration_min, avg_speed, high_speed_running_m }
   * @param {number} weight - Player weight in kg
   */
  calculateGpsCalories(gpsData, weight) {
    // MET values for football activities
    const baseMet = 8.0; // Moderate football training
    const highIntensityMet = 10.5; // High-intensity portions

    // Calculate high-intensity percentage
    const hsrPercent = (gpsData.high_speed_running_m || 0) / ((gpsData.distance_km || 1) * 1000);

    // Weighted MET
    const avgMet = baseMet * (1 - hsrPercent) + highIntensityMet * hsrPercent;

    // Calories = MET × weight (kg) × duration (hours)
    const durationHours = (gpsData.duration_min || 0) / 60;
    const calories = Math.round(avgMet * weight * durationHours);

    return {
      estimated_calories: calories,
      met_used: avgMet.toFixed(1),
      intensity_level: hsrPercent > 0.15 ? "high" : hsrPercent > 0.08 ? "moderate" : "low",
      breakdown: {
        base_burn: Math.round(baseMet * weight * durationHours),
        intensity_addition: Math.round((avgMet - baseMet) * weight * durationHours),
      },
    };
  },

  /**
   * Send team nutrition reminder
   * @param {number} clubId - Club ID
   * @param {number|null} teamId - Team ID (optional)
   * @param {string} message - Custom reminder message
   */
  sendTeamNutritionReminder(clubId, teamId = null, message = null) {
    return HTTP.post(API_SERVER, {
      action: "sendTeamNutritionReminder",
      club_id: clubId,
      team_id: teamId,
      message: message,
    });
  },

  /**
   * Send individual player nutrition reminder
   * @param {number} playerId - Player user ID
   * @param {string} message - Reminder message
   */
  sendPlayerNutritionReminder(playerId, message = null) {
    return HTTP.post(API_SERVER, {
      action: "sendPlayerNutritionReminder",
      player_id: playerId,
      message: message,
    });
  },

  /**
   * Export nutrition compliance report
   * @param {number} clubId - Club ID
   * @param {number|null} teamId - Team ID (optional)
   * @param {Object} dateRange - { start, end } dates
   * @param {string} format - Export format (csv, pdf)
   */
  exportComplianceReport(clubId, teamId = null, dateRange, format = "csv") {
    return HTTP.post(API_SERVER, {
      action: "exportComplianceReport",
      club_id: clubId,
      team_id: teamId,
      start_date: dateRange.start,
      end_date: dateRange.end,
      format: format,
    });
  },

  /**
   * Get match day nutrition preparation status
   * @param {number} teamId - Team ID
   * @param {number} gameId - Game ID
   */
  getMatchDayNutritionPrep(teamId, gameId) {
    return HTTP.post(API_SERVER, {
      action: "getMatchDayNutritionPrep",
      team_id: teamId,
      game_id: gameId,
    });
  },

  // =========================================================================
  // PHASE 3: UTILITY METHODS
  // =========================================================================

  /**
   * Calculate BMR using Mifflin-St Jeor equation
   * @param {number} weight - Weight in kg
   * @param {number} height - Height in cm
   * @param {number} age - Age in years
   * @param {string} gender - 'male' or 'female'
   */
  calculateBMR(weight, height, age, gender = "male") {
    if (gender === "male") {
      return Math.round(10 * weight + 6.25 * height - 5 * age + 5);
    }
    return Math.round(10 * weight + 6.25 * height - 5 * age - 161);
  },

  /**
   * Get activity multiplier based on training context
   * @param {string} context - Day context (training_day, match_day, rest_day, etc.)
   */
  getActivityMultiplier(context) {
    const multipliers = {
      rest_day: 1.2,
      light_training: 1.5,
      training_day: 1.7,
      heavy_training: 1.9,
      preparation: 1.8,
      carb_loading: 1.7,
      match_day: 2.0,
      recovery_day: 1.4,
    };
    return multipliers[context] || 1.5;
  },

  /**
   * Get recommended macro distribution for context
   * @param {string} context - Day context
   */
  getMacroDistribution(context) {
    const distributions = {
      match_day: { carbs: 60, protein: 15, fat: 25 },
      carb_loading: { carbs: 65, protein: 15, fat: 20 },
      preparation: { carbs: 55, protein: 20, fat: 25 },
      recovery_day: { carbs: 50, protein: 25, fat: 25 },
      training_day: { carbs: 50, protein: 25, fat: 25 },
      heavy_training: { carbs: 55, protein: 25, fat: 20 },
      rest_day: { carbs: 40, protein: 30, fat: 30 },
    };
    return distributions[context] || distributions.training_day;
  },

  /**
   * Get supplement recommendations based on player status
   * @param {Object} playerData - Player profile and training data
   */
  getSupplementRecommendations(playerData) {
    const recommendations = [];

    // Base recommendations for all football players
    recommendations.push({
      id: "creatine",
      name: "Creatine Monohydrate",
      priority: "primary",
      dosage: "3-5g daily",
      timing: "Any time, consistent",
      evidence: "strong",
    });

    recommendations.push({
      id: "protein",
      name: "Whey Protein",
      priority: "primary",
      dosage: "20-40g post-training",
      timing: "Within 30-60 min of training",
      evidence: "strong",
    });

    // Vitamin D (especially for UK-based players)
    recommendations.push({
      id: "vitamin_d",
      name: "Vitamin D3",
      priority: "primary",
      dosage: "1000-2000 IU daily",
      timing: "Morning with food",
      evidence: "strong",
    });

    // Context-based recommendations
    if (playerData?.context === "match_day") {
      recommendations.push({
        id: "caffeine",
        name: "Caffeine",
        priority: "secondary",
        dosage: "3-6mg/kg body weight",
        timing: "30-60 min before kick-off",
        evidence: "strong",
      });

      recommendations.push({
        id: "beta_alanine",
        name: "Beta-Alanine",
        priority: "secondary",
        dosage: "3-6g daily (loading phase)",
        timing: "Split doses to minimize tingling",
        evidence: "moderate",
      });
    }

    if (playerData?.isInjured) {
      recommendations.push({
        id: "omega3",
        name: "Omega-3 (EPA/DHA)",
        priority: "secondary",
        dosage: "2-4g EPA+DHA daily",
        timing: "With meals",
        evidence: "moderate",
      });
    }

    return recommendations;
  },
};

export { Nutrition };
export default Nutrition;
